import { useDisclosure } from '@chakra-ui/react'
import { UseQueryResult } from '@tanstack/react-query'
import cn from 'clsx'
import { Dispatch, FC, SetStateAction } from 'react'

import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'

import { ITranslateParams } from '../File'
import { TFileLeftTab } from '../helpers/getNextFileTabs'

import TextContainer from './TextContainer'
import UploadOriginalText from './UploadOriginalText'

type Disclosure = ReturnType<typeof useDisclosure>

interface IFileOriginalColumnProps {
	shotlist?: IShotlist
	hasAvailableShotlistLanguages: boolean
	leftRenderLanguage?: IShotlist['original']
	setIsLoading: (value: boolean) => void
	refetch: () => Promise<unknown>
	translationLanguages: string[]
	leftTab: TFileLeftTab
	setLeftTab: (value: TFileLeftTab) => void
	isTranslateLoading: boolean
	setIsTranslateLoading: (value: boolean) => void
	setRightTab: (value: string) => void
	isTranslateError: boolean
	setIsTranslateError: (value: boolean) => void
	lastTranslateParams: ITranslateParams | null
	setLastTranslateParams: (params: ITranslateParams) => void
	isChoosingLanguage: boolean
	setIsChoosingLanguage: (value: boolean) => void
	targetLang: string | null
	setTargetLang: (value: string) => void
	originalSourceName: string

	languages: ILanguage[]
	onUploadSrt: () => void
	selectedTranscribingLang: ILanguage | null
	setSelectedTranscribingLang: Dispatch<SetStateAction<ILanguage | null>>
	selectedSrtLang: ILanguage | null
	setSelectedSrtLang: Dispatch<SetStateAction<ILanguage | null>>
	setSelectedSrtFile: Dispatch<SetStateAction<File | null>>
	selectLanguageDialog: Disclosure
	selectSrtDialog: Disclosure
	clearWatch: () => void
	refetchShotlist: UseQueryResult<IShotlist, unknown>['refetch']
	setIsTranscribing: Dispatch<SetStateAction<boolean>>
	watchTask: (
		taskId: string,
		onUpdate?: (shotlist: IShotlist) => void,
		onFinished?: (shotlist: IShotlist) => void
	) => Promise<void>
}

const FileOriginalColumn: FC<IFileOriginalColumnProps> = ({
	shotlist,
	hasAvailableShotlistLanguages,
	leftRenderLanguage,
	setIsLoading,
	refetch,
	translationLanguages,
	leftTab,
	setLeftTab,
	isTranslateLoading,
	setIsTranslateLoading,
	setRightTab,
	isTranslateError,
	setIsTranslateError,
	lastTranslateParams,
	setLastTranslateParams,
	isChoosingLanguage,
	setIsChoosingLanguage,
	targetLang,
	setTargetLang,
	originalSourceName,
	languages,
	onUploadSrt,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	selectedSrtLang,
	setSelectedSrtLang,
	setSelectedSrtFile,
	selectLanguageDialog,
	selectSrtDialog,
	clearWatch,
	refetchShotlist,
	setIsTranscribing,
	watchTask
}) => {
	return (
		<section
			className={cn(
				hasAvailableShotlistLanguages ? 'flex-[0_1_37.5%]' : 'flex-[0_1_75%]'
			)}
		>
			{shotlist?.original?.content ? (
				<TextContainer
					key={leftRenderLanguage?.id || 'original'}
					id={shotlist?.original?.id}
					type='original'
					textArray={leftRenderLanguage?.content?.split('\n\n') || []}
					setIsLoading={setIsLoading}
					onTranslate={refetch}
					language={translationLanguages}
					setTab={setLeftTab}
					tab={leftTab}
					isTranslateLoading={isTranslateLoading}
					setIsTranslateLoading={setIsTranslateLoading}
					setRightTab={setRightTab}
					originalLang={shotlist?.original?.language}
					isTranslateError={isTranslateError}
					setIsTranslateError={setIsTranslateError}
					lastTranslateParams={lastTranslateParams}
					setLastTranslateParams={setLastTranslateParams}
					isChoosingLanguage={isChoosingLanguage}
					setIsChoosingLanguage={setIsChoosingLanguage}
					targetLang={targetLang}
					setTargetLang={setTargetLang}
					showChooser={false}
					originalSourceName={originalSourceName}
				/>
			) : (
				<UploadOriginalText
					taskId={shotlist?.id}
					languages={languages}
					onUploadSrt={onUploadSrt}
					selectedTranscribingLang={selectedTranscribingLang}
					setSelectedTranscribingLang={setSelectedTranscribingLang}
					selectedSrtLang={selectedSrtLang}
					setSelectedSrtLang={setSelectedSrtLang}
					setSelectedSrtFile={setSelectedSrtFile}
					selectLanguageDialog={selectLanguageDialog}
					selectSrtDialog={selectSrtDialog}
					isTranslation={shotlist?.translations?.length > 0}
					clearWatch={clearWatch}
					refetchShotlist={refetchShotlist}
					setIsTranscribing={setIsTranscribing}
					watchTask={watchTask}
				/>
			)}
		</section>
	)
}

export default FileOriginalColumn
